import { useLayoutEffect, useRef } from 'react';
import { AGENDA_COLUMNS } from '../agendaConfig';
import type { useAgendaChainPlacement } from '../useAgendaChainPlacement';

export function AgendaChainPlacementConfirmation({ placement }: { placement: ReturnType<typeof useAgendaChainPlacement> }) {
  const ref = useRef<HTMLDivElement>(null), backRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    backRef.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);
  if (!placement.moving || !placement.pendingTarget) return null;
  const { info } = placement.moving, target = placement.pendingTarget;
  const time = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
  const provider = (id: string) => AGENDA_COLUMNS.find((column) => column.practitionerId === id)?.label || id;
  const result = placement.check(target);
  const back = () => { if (!placement.saving) { placement.setPendingTarget(null); placement.setError(''); } };
  return (
    <div className="agenda-move-confirmation-overlay" onClick={back}>
      <div ref={ref} className="agenda-appointment-info agenda-move-confirmation" role="dialog" aria-modal="true" aria-labelledby="agenda-chain-placement-title"
        onClick={(event) => event.stopPropagation()} onKeyDown={(event) => {
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); back(); }
          if (event.key === 'Tab') {
            const controls = ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled)');
            const first = controls?.[0], last = controls?.[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          }
        }}>
        <h2 id="agenda-chain-placement-title">Serientermin hier platzieren?</h2>
        <p>Möchten Sie diesen Termin an der ausgewählten Position in die Terminserie übernehmen?</p>
        <dl>
          <dt>Patient</dt><dd>{info.patientName}</dd>
          <dt>Behandlung</dt><dd>{info.title}</dd>
          <dt>Datum</dt><dd>{target.date.split('-').reverse().join('.')}</dd>
          <dt>Startzeit</dt><dd>{time(target.minute)} Uhr</dd>
          <dt>Endzeit</dt><dd>{time(target.minute + info.duration)} Uhr</dd>
          <dt>Dauer</dt><dd>{info.duration} Minuten</dd>
          <dt><label htmlFor="agenda-chain-placement-provider">Behandler</label></dt>
          <dd>{target.candidates.length > 1 ? (
            <select id="agenda-chain-placement-provider" value={target.practitionerId} disabled={placement.saving}
              onChange={(event) => { placement.setPendingTarget({ ...target, practitionerId: event.target.value }); placement.setError(''); }}>
              <option value="">Bitte auswählen</option>
              {target.candidates.map((id) => <option key={id} value={id}>{provider(id)}</option>)}
            </select>
          ) : provider(target.practitionerId)}</dd>
        </dl>
        <p className="agenda-move-confirmation__validation" role="status">{target.practitionerId ? result.message : 'Bitte wählen Sie den Behandler ausdrücklich aus.'}</p>
        {placement.error ? <p className="agenda-move-confirmation__error" role="alert">{placement.error}</p> : null}
        <div className="agenda-move-confirmation__actions">
          <button ref={backRef} type="button" onClick={back} disabled={placement.saving}>Abbrechen</button>
          <button type="button" className="agenda-move-confirmation__submit" onClick={() => void placement.confirm()}
            disabled={placement.saving || !target.practitionerId || result.status !== 'valid'}>{placement.saving ? 'Wird geprüft…' : 'Bestätigen'}</button>
        </div>
      </div>
    </div>
  );
}
